'use client';

import { User } from '@supabase/supabase-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { ChangePasswordFormValues } from '@/schemas/change-password/change-password';
import { trackCheckoutStageAction } from '@/actions/ghl/track-checkout-stage';

import { supabase } from '@/lib/supabase/client';
import { paths } from '@/constants/paths';
import { queryKeys } from '@/constants/query-keys';

import { env } from '@/env';
type SignUpProps = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  priceId?: string;
  isFreePlan?: boolean;
};
export function useSignUp() {
  return useMutation({
    mutationFn: async ({
      firstName,
      lastName,
      email,
      password,
      priceId,
      isFreePlan,
    }: SignUpProps) => {
      // if (!refId) throw new Error('Referral ID not found');

      // Only require priceId if they are provided
      if (priceId && !priceId.trim()) {
        throw new Error('Invalid price ID');
      }

      const successUrl = `${env.NEXT_PUBLIC_APP_URL}${paths.checkout.success}?source=self`;
      const checkoutUrl = `${env.NEXT_PUBLIC_APP_URL}${paths.checkout.index}?priceId=${priceId}&email=${email}&name=${email?.split('@')[0]}&successUrl=${successUrl}`;

      // Set email redirect based on signup type
      const emailRedirectTo =
        priceId && !isFreePlan
          ? checkoutUrl
          : `${env.NEXT_PUBLIC_APP_URL}${paths.onboarding}`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo,
          data: {
            firstName,
            lastName,
          },
        },
      });

      if (error) throw new Error(error.message);

      if (!data.user?.id) throw new Error('User ID not found');
      // * add to referral table
      // const { error: referralError } = await supabase.from('referrals').insert({
      //   user_id: data.user.id,
      //   ref_id: refId,
      // });

      // if (referralError) throw new Error(referralError.message);
      if (priceId) {
        // Track registration in GHL (Event 1: Registered - Not Verified)
        try {
          await trackCheckoutStageAction({
            email,
            priceId,
            stage: 'Registered - Not Verified',
            name: email?.split('@')[0],
          });
        } catch (ghlError) {
          // Don't block signup if GHL tracking fails
          console.error('Failed to track registration in GHL:', ghlError);
        }
      }

      return data;
    },
  });
}

export function useSignIn() {
  const router = useRouter();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      router.push('/dashboard');
      router.refresh();
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}${paths.api.callback.resetPassword}`,
      });

      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async ({ password }: { password: string }) => {
      const { data, error } = await supabase.auth.updateUser({
        password,
      });

      if (error) throw new Error(error.message);
      return data;
    },
  });
}
export function useResendEmail() {
  const [countdown, setCountdown] = useState(0);
  const [resendCount, setResendCount] = useState(0);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const resendMutation = useMutation({
    mutationFn: async () => {
      if (resendCount >= 2) {
        throw new Error('Maximum resend attempts reached');
      }
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: localStorage.getItem('verificationEmail') || '',
      });

      if (error) throw error;
      setResendCount(resendCount + 1);
      setCountdown(60);
    },
    onSuccess: () => {
      toast.success('Verification email resent');
    },
    onError: (error) => {
      toast.error('Failed to resend verification email', {
        description: error.message,
      });
    },
  });
  return {
    countdown,
    resendCount,
    resendMutation,
  };
}

export function useSignOut() {
  const router = useRouter();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut();

      if (error) throw new Error(error.message);
      return null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      router.push(paths.auth.login);
      router.refresh();
    },
  });
}

// export function useUser() {
//   const [user, setUser] = useState<User | null>(null);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     if (!supabase) return;

//     async function getUser() {
//       try {
//         const { data, error } = await supabase.auth.getUser();
//         if (error) throw error;
//         setUser(data.user);
//       } catch (err) {
//         console.error('Error getting user:', err);
//       } finally {
//         setLoading(false);
//       }
//     }

//     getUser();

//     // Set up auth state listener
//     const {
//       data: { subscription },
//     } = supabase.auth.onAuthStateChange((_event, session) => {
//       setUser(session?.user ?? null);
//     });

//     return () => {
//       subscription.unsubscribe();
//     };
//   }, [supabase]);

//   return { user, loading };
// }

export const useUser = () => {
  const queryClient = useQueryClient();
  const { data: user, isLoading } = useQuery<User | null>({
    queryKey: [queryKeys.user],
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      return user;
    },
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      queryClient.setQueryData([queryKeys.user], session?.user ?? null);
    });

    return () => data.subscription.unsubscribe();
  }, []);

  return { user, loading: isLoading };
};

export function useChangePassword() {
  return useMutation({
    mutationFn: async (values: ChangePasswordFormValues) => {
      try {
        const {
          data: { user },
          error,
        } = await supabase.auth.getUser();

        if (error) throw new Error('Sign in to change password');
        if (!user?.id) throw new Error('Sign in to change password');

        const { data, error: changePasswordError } = await supabase.rpc(
          'change_password',
          {
            current_plain_password: values.oldPassword,
            new_plain_password: values.newPassword,
            current_id: user.id,
          },
        );

        if (changePasswordError)
          throw new Error(
            changePasswordError.message || 'Failed to change password',
          );

        if (data === 'incorrect') throw new Error('Old password is incorrect');

        return data;
      } catch (err: unknown) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : 'Failed to change password';
        throw new Error(message);
      }
    },
  });
}
